import { BusinessProfileSchema, type BusinessProfile, type GrowthPlatform, type UpsertBusinessProfileRequest } from "./growth";
import type { SellerProfile } from "./index";

export const PROFILE_PLATFORMS = ["TikTok Shop", "Temu", "Shopee", "AliExpress", "SHEIN", "Amazon", "独立站"] as const;
export const PROFILE_MARKETS = ["美国", "加拿大", "墨西哥", "英国", "欧盟", "东南亚", "日本", "韩国", "澳大利亚", "中东", "拉丁美洲"] as const;
export const PROFILE_GOALS = ["完成首个店铺注册和商品上架", "找到第一个适合销售的商品", "完成第一次商品利润核算", "完成第一次供应商筛选", "完成第一个商品上架", "获得第一个有效订单"] as const;
export const PROFILE_EXPERIENCE = { preparing: "正在准备", beginner: "刚起步", operating: "已经开始经营", scaling: "准备放大经营" } as const;
export const PROFILE_BUSINESS = { individual: "个人", sole_proprietor: "个体工商户", company: "企业" } as const;
export const GROWTH_PLATFORM_LABELS: Record<GrowthPlatform, string> = { tiktok_shop: "TikTok Shop", temu: "Temu", shopee: "Shopee" };

export function emptyBusinessProfile(): BusinessProfile {
  return BusinessProfileSchema.parse({ platforms: [], selectedPlatform: null, targetMarkets: [], countries: [], categories: [], experienceStage: null,
    businessType: null, budgetCents: null, weeklyHours: null, goals: [], constraints: [], diagnosticCompleted: false, updatedAt: new Date(0).toISOString() });
}

export interface ProfileFormValues {
  platforms: string[]; markets: string[]; countryText: string; categoryText: string;
  experienceStage: NonNullable<BusinessProfile["experienceStage"]>; businessType: NonNullable<BusinessProfile["businessType"]>;
  budgetYuan: string; weeklyHours: string; goal: string;
}

export function profileToForm(profile: BusinessProfile): ProfileFormValues {
  return {
    platforms: profile.platforms.length ? profile.platforms : profile.selectedPlatform ? [GROWTH_PLATFORM_LABELS[profile.selectedPlatform]] : [],
    markets: profile.targetMarkets.length ? profile.targetMarkets : ["美国"], countryText: profile.countries.join("、"), categoryText: profile.categories.join("、"),
    experienceStage: profile.experienceStage ?? "preparing", businessType: profile.businessType ?? "individual",
    budgetYuan: String((profile.budgetCents ?? 100000) / 100), weeklyHours: String(profile.weeklyHours ?? 10), goal: profile.goals[0] ?? PROFILE_GOALS[0],
  };
}

export function splitProfileValues(value: string): string[] {
  return [...new Set(value.split(/[,，、\n]/).map(item => item.trim()).filter(Boolean))];
}

export function profileFormError(form: ProfileFormValues): string | null {
  if (!form.markets.length) return "请至少选择一个目标市场。";
  if (form.platforms.length > 12 || form.markets.length > 12) return "平台和目标市场分别最多选择 12 项。";
  if (form.budgetYuan.trim() === "" || !Number.isFinite(Number(form.budgetYuan)) || Number(form.budgetYuan) < 0) return "请填写有效的首期预算，最低为 0 元。";
  if (!Number.isInteger(Number(form.weeklyHours)) || Number(form.weeklyHours) < 1 || Number(form.weeklyHours) > 168) return "每周投入时间请填写 1 至 168 小时的整数。";
  if (!form.goal.trim()) return "请选择第一个目标。";
  if (splitProfileValues(form.countryText).length > 24 || splitProfileValues(form.categoryText).length > 20) return "目标国家最多 24 项，经营类目最多 20 项。";
  return null;
}

// Submit changed fields only, so an open form does not overwrite changes made on the other client.
export function profileFormPatch(form: ProfileFormValues, baseline: BusinessProfile): UpsertBusinessProfileRequest {
  const next: UpsertBusinessProfileRequest = {
    platforms: form.platforms, targetMarkets: form.markets, countries: splitProfileValues(form.countryText), categories: splitProfileValues(form.categoryText),
    experienceStage: form.experienceStage, businessType: form.businessType, budgetCents: Math.round(Number(form.budgetYuan) * 100),
    weeklyHours: Number(form.weeklyHours), goals: [form.goal, ...baseline.goals.slice(1)],
  };
  return Object.fromEntries(Object.entries(next).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(baseline[key as keyof BusinessProfile]))) as UpsertBusinessProfileRequest;
}

export function businessToSellerProfile(profile: BusinessProfile): SellerProfile {
  return { platforms: profile.platforms.length ? profile.platforms : profile.selectedPlatform ? [GROWTH_PLATFORM_LABELS[profile.selectedPlatform]] : [],
    markets: profile.targetMarkets, countries: profile.countries, categories: profile.categories,
    experienceStage: profile.experienceStage ?? undefined, businessType: profile.businessType ?? undefined };
}

// Legacy data is imported only after the user explicitly chooses to import it; account values win.
export function mergeLegacyProfile(account: BusinessProfile, legacy: SellerProfile): BusinessProfile {
  return { ...account, platforms: account.platforms.length ? account.platforms : legacy.platforms,
    targetMarkets: account.targetMarkets.length ? account.targetMarkets : legacy.markets,
    countries: account.countries.length ? account.countries : legacy.countries, categories: account.categories.length ? account.categories : legacy.categories,
    experienceStage: account.experienceStage ?? legacy.experienceStage ?? null, businessType: account.businessType ?? legacy.businessType ?? null };
}
